import { REPORTS_FUNCTION_PATH, REPORTS_PROTOCOL_VERSION, checkReportsResponse, encodeUploadMeta, type ReportUploadMeta, type ReportsResponse } from '../../supabase/functions/_shared/reportsProtocol';

// Client transport to reports-v2 (the cloud copies of delivered PDFs, Owner decision list 2026-10-19
// items 25 / 27 / 28). Same discipline as billingClient.ts: the user's JWT on every call, outcomes as codes.
export type ReportsFailureCode = 'offline' | 'network' | 'timeout' | 'notConfigured' | 'unauthenticated' | 'storageUnavailable' | 'rejected' | 'malformedResponse';

export type ReportsCallOutcome = { kind: 'ok'; response: Extract<ReportsResponse, { ok: true }> } | { kind: 'failed'; code: ReportsFailureCode; message: string };

export interface ReportsTransport {
  upload(token: string | null, meta: ReportUploadMeta, bytes: ArrayBuffer): Promise<ReportsCallOutcome>;
  list(token: string | null): Promise<ReportsCallOutcome>;
  link(token: string | null, fileId: string): Promise<ReportsCallOutcome>;
}

function endpoint(): string | null {
  const base = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').trim().replace(/\/$/, '');
  return base.length > 0 ? `${base}${REPORTS_FUNCTION_PATH}` : null;
}

function anonKey(): string {
  return ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim();
}

async function send(url: string, init: RequestInit, timeoutMs: number): Promise<ReportsCallOutcome> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(url, { ...init, signal: controller.signal });
  } catch (raw) {
    clearTimeout(timer);
    const aborted = raw instanceof Error && raw.name === 'AbortError';
    return { kind: 'failed', code: aborted ? 'timeout' : 'network', message: aborted ? 'reports request timed out' : 'network error' };
  }
  clearTimeout(timer);
  let parsed: unknown = null;
  try {
    parsed = (await res.json()) as unknown;
  } catch {
    parsed = null;
  }
  const checked = checkReportsResponse(parsed);
  if (!checked) return { kind: 'failed', code: res.status === 404 ? 'notConfigured' : 'malformedResponse', message: `unexpected answer (HTTP ${res.status})` };
  if (!checked.ok) {
    const code: ReportsFailureCode = checked.code === 'unauthenticated' ? 'unauthenticated' : checked.code === 'storageUnavailable' ? 'storageUnavailable' : 'rejected';
    return { kind: 'failed', code, message: checked.message };
  }
  return { kind: 'ok', response: checked };
}

function ready(token: string | null): ReportsCallOutcome | null {
  if (!endpoint() || anonKey().length === 0) return { kind: 'failed', code: 'notConfigured', message: 'reports backend is not configured' };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { kind: 'failed', code: 'offline', message: 'device is offline' };
  if (!token) return { kind: 'failed', code: 'unauthenticated', message: 'no session token' };
  return null;
}

export const fetchReportsTransport: ReportsTransport = {
  async upload(token, meta, bytes) {
    const blocked = ready(token);
    if (blocked) return blocked;
    return send(`${endpoint()}?action=upload`, { method: 'POST', headers: { 'Content-Type': 'application/pdf', apikey: anonKey(), Authorization: `Bearer ${token}`, 'X-Report-Meta': encodeUploadMeta(meta) }, body: bytes }, 60_000);
  },
  async list(token) {
    const blocked = ready(token);
    if (blocked) return blocked;
    return send(endpoint()!, { method: 'POST', headers: { 'Content-Type': 'application/json', apikey: anonKey(), Authorization: `Bearer ${token}` }, body: JSON.stringify({ protocolVersion: REPORTS_PROTOCOL_VERSION, action: 'list' }) }, 15_000);
  },
  async link(token, fileId) {
    const blocked = ready(token);
    if (blocked) return blocked;
    return send(endpoint()!, { method: 'POST', headers: { 'Content-Type': 'application/json', apikey: anonKey(), Authorization: `Bearer ${token}` }, body: JSON.stringify({ protocolVersion: REPORTS_PROTOCOL_VERSION, action: 'link', fileId }) }, 15_000);
  }
};

export const unavailableReportsTransport: ReportsTransport = {
  upload: async () => ({ kind: 'failed', code: 'notConfigured', message: 'reports backend is not configured' }),
  list: async () => ({ kind: 'failed', code: 'notConfigured', message: 'reports backend is not configured' }),
  link: async () => ({ kind: 'failed', code: 'notConfigured', message: 'reports backend is not configured' })
};

let override: ReportsTransport | null = null;

export function setReportsTransportForTests(transport: ReportsTransport | null): void {
  override = transport;
}

export function getReportsTransport(): ReportsTransport {
  if (override) return override;
  return endpoint() && anonKey().length > 0 ? fetchReportsTransport : unavailableReportsTransport;
}
